import React from 'react';
import { useAISignals } from '@/hooks/use-ai-signals';
import { AISignal } from '@/lib/ai-signals-api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RefreshCw, ExternalLink, Info, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AISignalsInfluenceProps {
  assets: string[];
  signalInfluence?: Record<string, { signal: AISignal; weightChange: number }>;
}

export default function AISignalsInfluence({ assets, signalInfluence }: AISignalsInfluenceProps) {
  const { signals, isLoading, error, refetch } = useAISignals({
    symbol: assets.join(',')
  });

  const getSignalTypeColor = (signalType: string) => {
    switch (signalType) {
      case 'BUY': return 'bg-green-500';
      case 'SELL': return 'bg-red-500';
      case 'HOLD': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getConfidenceLevel = (confidence: number) => {
    if (confidence >= 0.8) return 'HIGH';
    if (confidence >= 0.6) return 'MEDIUM';
    return 'LOW';
  };

  const truncateReasoning = (reasoning: string, maxLength: number = 100) => {
    if (reasoning.length <= maxLength) return reasoning;
    return reasoning.substring(0, maxLength) + '...';
  };

  const handleRefresh = () => {
    refetch();
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>AI Signals Influence</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-40" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !signals.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>AI Signals Influence</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <Zap className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">No AI Signals Yet</h3>
            <p className="text-muted-foreground max-w-sm mb-4">
              {error ? 'Failed to load AI signals' : 'No AI signals available for portfolio assets'}
            </p>
            <Button onClick={handleRefresh} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>AI Signals Influence</CardTitle>
          <Button onClick={handleRefresh} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <TooltipProvider>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Asset</TableHead>
                <TableHead>Signal Type</TableHead>
                <TableHead>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex items-center gap-1 cursor-help">
                        Confidence Score
                        <Info className="w-3 h-3" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Confidence level: {getConfidenceLevel(signals[0]?.confidence_score || 0)}</p>
                    </TooltipContent>
                  </Tooltip>
                </TableHead>
                <TableHead>Price Target</TableHead>
                <TableHead>Reasoning</TableHead>
                <TableHead>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex items-center gap-1 cursor-help">
                        Influence on Weight
                        <Info className="w-3 h-3" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>How AI signals adjust Black-Litterman expected returns</p>
                    </TooltipContent>
                  </Tooltip>
                </TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {signals.map((signal) => {
                const influence = signalInfluence?.[signal.symbol];
                const weightChange = influence?.weightChange || 0;

                return (
                  <TableRow key={signal.id}>
                    <TableCell className="font-medium">{signal.symbol}</TableCell>
                    <TableCell>
                      <Badge
                        className={cn(
                          "text-white",
                          getSignalTypeColor(signal.signal_type)
                        )}
                      >
                        {signal.signal_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress
                          value={signal.confidence_score * 100}
                          className="w-16 h-2"
                        />
                        <span className="text-sm">
                          {(signal.confidence_score * 100).toFixed(0)}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {signal.price_target ? (
                        <span className="font-mono">
                          ${signal.price_target.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help">
                            {truncateReasoning(signal.reasoning)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>{signal.reasoning}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <span className={cn(
                        "font-medium",
                        weightChange > 0 ? "text-green-600" : weightChange < 0 ? "text-red-600" : "text-gray-600"
                      )}>
                        {weightChange > 0 ? '+' : ''}{(weightChange * 100).toFixed(1)}%
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                      >
                        <a
                          href={`/ai-signals/${signal.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          View
                        </a>
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
}